import { InputHTMLAttributes } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";

interface InputTextProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  requiredMessage?: string;
  className?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  showError?: boolean;
}

export default function EmailField<FieldsType extends FieldValues>({
  name,
  label,
  control,
  required,
  requiredMessage,
  shouldUnregister,
  rules,
  inputProps = {},
  className,
  wrapperClassName = "",
  labelClassName,
  showError = true,
}: InputTextProps<FieldsType>) {
  return (
    <Controller
      name={name}
      shouldUnregister={shouldUnregister}
      control={control}
      rules={{
        required: requiredMessage || (required && "Required"),
        pattern: {
          value: /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          message: "Please enter a valid email",
        },
        ...(rules || {}),
      }}
      render={({ field, fieldState: { error } }) => (
        <div className={`relative ${wrapperClassName}`}>
          {label && (
            <label
              className={`inline-block text-gray-500 pb-1 text-sm ${labelClassName}`}
            >
              {label}
              {(required || requiredMessage) && (
                <span className="text-red-600 ml-0.5 text-base">*</span>
              )}
            </label>
          )}
          <div className="flex-1 w-full">
            <input
              id={field.name}
              className={`w-full appearance-none border border-gray-300 px-3 bg-gray-50 py-2.5 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:shadow-none focus:ring-0 text-sm ${className} ${
                error?.message ? "border-red-500 focus:border-red-500" : "focus:border-gray-900"
              }`}
              type="email"
              inputMode="email"
              placeholder="example@email.com"
              {...field}
              {...(inputProps || {})}
            />
            {showError && error?.message && (
              <p className="text-red-600 font-normal text-xs m-0 p-0 ml-1 mt-1">
                {error?.message}
              </p>
            )}
          </div>
        </div>
      )}
    />
  );
}
